import debounce from 'lodash.debounce';
import { LngLatBounds } from 'mapbox-gl';
import numeral from 'numeral';

import { API_ERROR_SEARCH } from '@/constants/messages';
import {
    PROJ_UTM_ZONELESS,
    PROJ_WGS84,
    SEARCH_TYPE_GEONAMES,
    SEARCH_TYPE_LAT_LONG,
    SEARCH_TYPE_UTM,
    SEARCH_TYPE_WFI,
} from '@/constants/search';

/**
 * event handlers for handling search results
 */
export default {

    data: () => ({
        loadingSearch: false,
        searchResults: [],
    }),

    methods: {
        /**
         * Format latitude/longitude degree numbers for display
         *
         * @param {number} degrees - lat/long degree number
         * @returns {string} - formatted display number for search results
         */
        coordsDisplayFormat(degrees) {
            return numeral(degrees).format('0.000');
        },

        /**
         * Move map to the new target location with optionally specified zoom level
         *
         * @param {object} geojson - geojson object which we want to focus on
         * @param {number} [zoomLevel = 9] - optional map zoom level
         */
        goToLocation(geojson, zoomLevel = 9) {
            if ('type' in geojson && geojson.type !== 'Point') {
                // likely a set of coordinates!
                // generate a bounding box from the list of points in the coordinates.
                const bounds = new LngLatBounds();
                geojson.coordinates.forEach(coord => coord.forEach(latlng => bounds.extend(latlng)));
                this.map.fitBounds(bounds, {
                    padding: {
                        top: 40,
                        left: 40,
                        right: 40,
                        bottom: 200, // we need 200 px padding on the bottom for the popup
                    },
                });
            } else {
                // might be a lngLat object, likely from the "location finder button"
                let coords = geojson;

                // check to make sure it's not a Point geojson object though!
                if ('type' in geojson && geojson.type === 'Point') {
                    coords = geojson.coordinates;
                }

                // make sure we actually have coordinates.
                if (coords.length) {
                    // straight up lngLat object, likely from the "location finder button"
                    this.map.flyTo({
                        center: coords,
                        zoom: zoomLevel,
                    });
                }
            }
        },

        /**
         * Return search results for lat/long coordinates
         *
         * @param {string} query - search string with "Lat, Long"
         * @returns {Array} - search results for search component
         */
        searchLatLong(query) {
            const latLongRegex = /^(-?\d{1,2}(\.\d+)?)[,\s]+(-?\d{1,3}(\.\d+)?)$/;
            const parsed = query.match(latLongRegex);

            // no results for invalid syntax
            if (parsed === null) return [];

            const lat = +parsed[1];
            const lng = +parsed[3];

            // no results for invalid coords
            if (lat < -90 || lat > 90) return [];

            // return clickable search result to go to coordinates
            return [
                {
                    title: 'Go to coordinates',
                    subtitle: `${this.coordsDisplayFormat(lat)} ${this.coordsDisplayFormat(lng)}`,
                    conciscode: 'COORDINATES',
                    lng,
                    lat,
                    geojson: {
                        type: 'Point',
                        coordinates: [
                            lng,
                            lat,
                        ],
                    },
                },
            ];
        },

        /**
         * Return search results for UTM coordinates
         *
         * @param {string} query - search string with UTM coordinates
         * @returns {Promise} - promise resolving to search results array
         */
        async searchUTM(query) {
            // parse input: <zone> <easting> <northing>
            const utmRegex = /^(\w+)\s+(-?\d+)m? ?E\s+(-?\d+)m? ?N$/;
            const parsed = query.match(utmRegex);

            // no results for invalid syntax
            if (parsed === null) return [];

            const zone = parsed[1];
            const easting = +parsed[2];
            const northing = +parsed[3];

            // load proj4 library on demand
            const { default: proj4 } = await import('proj4');

            // convert UTM coordinates to lng/lat
            const utmProj = `${PROJ_UTM_ZONELESS} +zone=${zone}`;
            const [lng, lat] = proj4(utmProj, PROJ_WGS84, [easting, northing]);

            // return clickable search result to go to coordinates
            return [
                {
                    title: 'Go to coordinates',
                    subtitle: `${this.coordsDisplayFormat(lat)} ${this.coordsDisplayFormat(lng)}`,
                    conciscode: 'COORDINATES',
                    lng,
                    lat,
                    geojson: {
                        type: 'Point',
                        coordinates: [
                            lng,
                            lat,
                        ],
                    },
                },
            ];
        },

        /**
         * Handle user input in the search bar
         *
         * @param {string} query - search string, usually provided by the search-bar
         * @param {object} type - search type to perform (geonames, features, coordinates...)
         */
        searchBarTyping: debounce(async function debouncedSearch(query, type) {
            this.loadingSearch = true;
            const searchType = type.label;

            // reset search results
            this.searchResults = [];

            try {
                if (searchType === SEARCH_TYPE_GEONAMES) {
                    // geonames search
                    this.searchResults = await this.$api.getGeonamesSearch(query);
                } else if (searchType === SEARCH_TYPE_LAT_LONG) {
                    // lat, long coordinates search
                    this.searchResults = this.searchLatLong(query);
                } else if (searchType === SEARCH_TYPE_WFI) {
                    this.searchResults = await this.$api.getWFISearch(query);
                } else if (searchType === SEARCH_TYPE_UTM) {
                    // UTM ("easting + northing") coordinates search
                    this.searchResults = await this.searchUTM(query);
                }
            } catch (error) {
                // search API error
                this.$handleError(error, API_ERROR_SEARCH);
            } finally {
                this.loadingSearch = false;
            }
        }, 400),

        /**
         * Resets page component ("module") state on search input.
         * Assumes any search input will initialize a new search query.
         *
         * @param {string} query - value of the search text input
         */
        searchInput(query) {
            // set loading state if the input isn't blank
            if (query) this.loadingSearch = true;

            // clear search results
            // (a new search query will be performed when typing stops)
            // @see `searchBarTyping`
            this.searchResults = [];
        },
    },
};
